import React, { useEffect, useState } from "react";
import { ArrowRight, Play, ChevronDown } from "lucide-react";
import { useLanguage } from "../../i18n";
import { LOGO_HERO } from "../../constants";

// Premium hero background image
const HERO_BG = "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/zdmzsrqn_6807D169-3301-4818-92FC-61CA37977AB4.png";

const HeroSection = () => {
  const { language } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger animations after mount
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* DESKTOP VERSION */}
      <section className="hidden md:block relative h-screen w-full overflow-hidden" data-testid="hero-section-desktop">
        {/* Background Image with Cinematic Overlay */}
        <div className="absolute inset-0">
          <img 
            src={HERO_BG}
            alt="NexAir Elements"
            className="w-full h-full object-cover"
          />
          {/* Multi-layer cinematic overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60" />
          {/* Subtle vignette */}
          <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 100px rgba(0,0,0,0.5)' }} />
        </div>

        {/* Content Container */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-8">
          {/* Logo - HUGE, includes tagline and services */}
          <div 
            className={`mb-6 transition-all duration-1000 ease-out ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <img 
              src={LOGO_HERO}
              alt="NexAir Elements"
              className="h-72 md:h-80 lg:h-[420px] w-auto drop-shadow-[0_0_60px_rgba(0,240,255,0.3)]"
            />
          </div>

          {/* CTA Button - Single */}
          <div 
            className={`mb-8 transition-all duration-1000 delay-400 ease-out ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <a
              href="#contacto"
              className="group inline-flex items-center gap-3 bg-white text-black font-semibold py-4 px-12 rounded-full transition-all hover:bg-cyan-400 hover:shadow-[0_0_40px_rgba(0,240,255,0.5)]"
              data-testid="hero-cta-primary"
            >
              {language === 'es' ? 'Solicitar Propuesta' : 'Request Proposal'}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* Service Pills - with links */}
          <div 
            className={`flex items-center gap-4 transition-all duration-1000 delay-600 ease-out ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <a href="#experiencias" className="px-5 py-2 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 text-sm font-medium hover:bg-cyan-500/20 hover:border-cyan-400 transition-all">
              Drone Shows
            </a>
            <a href="#water-screen" className="px-5 py-2 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 text-sm font-medium hover:bg-cyan-500/20 hover:border-cyan-400 transition-all">
              Water Screens
            </a>
            <a href="#special-fx" className="px-5 py-2 rounded-full border border-purple-500/40 bg-purple-500/10 text-purple-400 text-sm font-medium hover:bg-purple-500/20 hover:border-purple-400 transition-all">
              Special FX
            </a>
            <a href="#sky-meets-water" className="px-5 py-2 rounded-full border border-blue-500/40 bg-blue-500/10 text-blue-400 text-sm font-medium hover:bg-blue-500/20 hover:border-blue-400 transition-all">
              Elements
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <a href="#problema" className="text-white/40 hover:text-white/70 transition-colors">
            <ChevronDown size={32} strokeWidth={1.5} />
          </a>
        </div>
      </section>

      {/* MOBILE VERSION */}
      <section className="md:hidden relative min-h-screen w-full overflow-hidden" data-testid="hero-section-mobile">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_BG}
            alt="NexAir Elements"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black" />
        </div>

        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col justify-center items-center px-6 py-20">
          {/* Logo - BIG on mobile too */}
          <div className="mb-6">
            <img 
              src={LOGO_HERO}
              alt="NexAir Elements"
              className="h-56 w-auto drop-shadow-[0_0_40px_rgba(0,240,255,0.3)]"
            />
          </div>

          {/* CTA Button - Single */}
          <div className="w-full max-w-xs mb-6">
            <a
              href="#contacto"
              className="flex items-center justify-center gap-2 bg-white text-black font-semibold py-4 rounded-full text-sm w-full"
              data-testid="hero-cta-mobile"
            >
              {language === 'es' ? 'Solicitar Propuesta' : 'Request Proposal'}
              <ArrowRight size={16} />
            </a>
          </div>

          {/* Service Pills - with links */}
          <div className="flex flex-wrap justify-center gap-2">
            <a href="#experiencias" className="px-4 py-2 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 text-xs font-medium">
              Drone Shows
            </a>
            <a href="#water-screen" className="px-4 py-2 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 text-xs font-medium">
              Water Screens
            </a>
            <a href="#special-fx" className="px-4 py-2 rounded-full border border-purple-500/40 bg-purple-500/10 text-purple-400 text-xs font-medium">
              Special FX
            </a>
            <a href="#sky-meets-water" className="px-4 py-2 rounded-full border border-blue-500/40 bg-blue-500/10 text-blue-400 text-xs font-medium">
              Elements
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
